import { useState, useRef } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Camera, SpinnerGap, CheckCircle, UploadSimple, Trash, Pencil } from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fc = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const CATEGORIES = [
  "Carnes, Aves e Peixes", "Laticínios e Ovos", "Hortifruti", "Secos e Grãos",
  "Confeitaria", "Panificação", "Temperos e Condimentos", "Óleos e Gorduras",
  "Bebidas e Líquidos", "Embalagens", "Outros",
];

export default function ReceiptScanner({ open, onOpenChange, onComplete }) {
  const [step, setStep] = useState("upload"); // upload | scanning | review | done
  const [imagePreview, setImagePreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [items, setItems] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleScan = async () => {
    if (!imagePreview) { toast.error("Selecione uma imagem"); return; }
    setScanning(true);
    setStep("scanning");
    try {
      const res = await axios.post(`${API}/stock/scan-receipt`, {
        image_base64: imagePreview,
      }, { timeout: 90000 });

      if (res.data.error) {
        toast.error(res.data.error);
        setStep("upload");
        return;
      }

      setReceiptData(res.data);
      setItems(
        (res.data.itens || []).map((item, idx) => ({
          _idx: idx,
          name: item.nome_insumo_limpo || item.nome_bruto_nota || "Item",
          name_raw: item.nome_bruto_nota || "",
          category: item.categoria_sugerida || "",
          quantity_purchased: item.quantidade || 1,
          unit: (item.unidade_medida || "kg").toLowerCase(),
          price: item.preco_total || 0,
          price_unit: item.preco_unitario || 0,
          matched_id: item.matched_ingredient_id || null,
          matched_name: item.matched_ingredient_name || null,
          is_new: item.is_new !== false,
          include: true,
        }))
      );
      setStep("review");
      toast.success(`${(res.data.itens || []).length} itens encontrados!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao escanear");
      setStep("upload");
    } finally {
      setScanning(false);
    }
  };

  const updateItem = (idx, field, val) => {
    setItems((prev) => prev.map((it) => (it._idx === idx ? { ...it, [field]: val } : it)));
  };

  const toggleItem = (idx) => {
    setItems((prev) => prev.map((it) => (it._idx === idx ? { ...it, include: !it.include } : it)));
  };

  const handleConfirm = async () => {
    const toConfirm = items.filter((it) => it.include);
    if (toConfirm.length === 0) { toast.error("Selecione pelo menos um item"); return; }
    setConfirming(true);
    try {
      const payload = {
        receipt_id: `scan-${Date.now()}`,
        items: toConfirm.map((it) => ({
          ingredient_id: it.matched_id,
          create_new: it.is_new && !it.matched_id,
          name: it.name,
          quantity_purchased: it.quantity_purchased,
          stock_quantity: it.quantity_purchased,
          unit: it.unit,
          price: it.price,
          category: it.category || null,
        })),
      };
      const res = await axios.post(`${API}/stock/confirm-scan`, payload);
      toast.success(res.data.message);
      setStep("done");
      onComplete?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao confirmar");
    } finally {
      setConfirming(false);
    }
  };

  const handleClose = () => {
    setStep("upload");
    setImagePreview(null);
    setReceiptData(null);
    setItems([]);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[750px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <Camera size={24} weight="fill" />
            {step === "upload" && "Escanear Nota / Recibo"}
            {step === "scanning" && "Processando..."}
            {step === "review" && "Revisar Itens"}
            {step === "done" && "Concluído!"}
          </DialogTitle>
          <DialogDescription className="sr-only">Scanner de notas</DialogDescription>
        </DialogHeader>

        {/* STEP 1: Upload */}
        {step === "upload" && (
          <div className="space-y-4 mt-2">
            <p className="text-sm text-zinc-600">
              Tire uma foto ou faça upload de uma nota fiscal / cupom de supermercado. O Sous-Chef IA vai extrair os itens automaticamente.
            </p>
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-zinc-300 rounded-lg p-8 text-center cursor-pointer hover:border-zinc-500 hover:bg-zinc-50 transition-colors"
            >
              {imagePreview ? (
                <img src={imagePreview} alt="Preview" className="max-h-[300px] mx-auto rounded" />
              ) : (
                <>
                  <UploadSimple size={40} className="mx-auto text-zinc-400 mb-3" />
                  <p className="text-sm font-semibold text-zinc-700">Clique para selecionar ou arrastar</p>
                  <p className="text-xs text-zinc-500 mt-1">PNG, JPG — Nota fiscal ou cupom</p>
                </>
              )}
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileSelect} capture="environment" />
            {imagePreview && (
              <div className="flex gap-2">
                <Button onClick={() => { setImagePreview(null); }} variant="outline" className="flex-1">Trocar Imagem</Button>
                <Button onClick={handleScan} className="flex-1 bg-zinc-950 text-white hover:bg-zinc-800" data-testid="scan-receipt-btn">
                  <Camera size={18} className="mr-2" /> Escanear com IA
                </Button>
              </div>
            )}
          </div>
        )}

        {/* STEP 2: Scanning */}
        {step === "scanning" && (
          <div className="text-center py-12">
            <SpinnerGap size={48} className="mx-auto text-zinc-500 animate-spin mb-4" />
            <p className="text-lg font-semibold text-zinc-700">Sous-Chef IA está lendo a nota...</p>
            <p className="text-sm text-zinc-500 mt-1">Extraindo itens, quantidades e preços</p>
          </div>
        )}

        {/* STEP 3: Review */}
        {step === "review" && (
          <div className="space-y-4 mt-2">
            {receiptData?.estabelecimento && (
              <p className="text-sm text-zinc-600">
                <strong>{receiptData.estabelecimento}</strong>
                {receiptData.data_compra && ` — ${receiptData.data_compra}`}
              </p>
            )}

            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-zinc-50">
                    <TableHead className="w-8"></TableHead>
                    <TableHead className="text-xs">Ingrediente</TableHead>
                    <TableHead className="text-xs">Categoria</TableHead>
                    <TableHead className="text-xs text-right">Qtd</TableHead>
                    <TableHead className="text-xs">Un</TableHead>
                    <TableHead className="text-xs text-right">Preço</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item._idx} className={!item.include ? "opacity-40" : ""}>
                      <TableCell>
                        <input type="checkbox" checked={item.include} onChange={() => toggleItem(item._idx)}
                          className="w-4 h-4 accent-zinc-900" />
                      </TableCell>
                      <TableCell>
                        <Input value={item.name} onChange={(e) => updateItem(item._idx, "name", e.target.value)}
                          className="h-7 text-xs w-full min-w-[120px]" />
                        {item.name_raw && item.name_raw !== item.name && (
                          <p className="text-[9px] text-zinc-400 mt-0.5 truncate" title={item.name_raw}>Nota: {item.name_raw}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <Select value={item.category} onValueChange={(v) => updateItem(item._idx, "category", v)}>
                          <SelectTrigger className="h-7 text-xs w-[130px]"><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent>
                            {CATEGORIES.map((c) => <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-right">
                        <Input type="number" step="0.001" value={item.quantity_purchased}
                          onChange={(e) => updateItem(item._idx, "quantity_purchased", parseFloat(e.target.value) || 0)}
                          className="h-7 text-xs w-16 text-right" />
                      </TableCell>
                      <TableCell>
                        <Select value={item.unit} onValueChange={(v) => updateItem(item._idx, "unit", v)}>
                          <SelectTrigger className="h-7 text-xs w-16"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="kg">kg</SelectItem>
                            <SelectItem value="l">l</SelectItem>
                            <SelectItem value="unidades">un</SelectItem>
                            <SelectItem value="g">g</SelectItem>
                            <SelectItem value="ml">ml</SelectItem>
                            <SelectItem value="maço">maço</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs">{fc(item.price)}</TableCell>
                      <TableCell>
                        {item.matched_name ? (
                          <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded" title={`Encontrado: ${item.matched_name}`}>
                            Existente
                          </span>
                        ) : (
                          <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                            Novo
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {receiptData?.valor_total_nota && (
              <div className="flex justify-between text-sm font-semibold p-2 bg-zinc-50 rounded">
                <span>Total da Nota</span>
                <span className="font-mono">{fc(receiptData.valor_total_nota)}</span>
              </div>
            )}

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep("upload")} className="flex-1">Voltar</Button>
              <Button onClick={handleConfirm} disabled={confirming}
                className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="confirm-receipt-btn">
                {confirming ? <SpinnerGap size={18} className="mr-2 animate-spin" /> : <CheckCircle size={18} className="mr-2" />}
                Confirmar e Dar Entrada no Estoque
              </Button>
            </div>
          </div>
        )}

        {/* STEP 4: Done */}
        {step === "done" && (
          <div className="text-center py-8">
            <CheckCircle size={48} className="mx-auto text-emerald-600 mb-3" weight="fill" />
            <p className="text-lg font-semibold text-zinc-900">Entrada concluída!</p>
            <p className="text-sm text-zinc-500 mt-1">Itens adicionados ao estoque com sucesso.</p>
            <Button onClick={handleClose} className="mt-4 bg-zinc-950 text-white hover:bg-zinc-800">Fechar</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
